import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  IconButton,
  Portal,
  Select,
  Separator,
  Switch,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineCheckCircle, AiOutlineWarning } from 'react-icons/ai';
import { BiFile } from 'react-icons/bi';
import { CiImport } from 'react-icons/ci';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import * as XLSX from 'xlsx';

import { EntitiesService, IndicatorYearPeriodPublic, IndicatorsPeriodsService, IndicatorsService } from '@/client';
import { Tooltip } from '@/components/ui/tooltip';
import useCustomToast from '@/hooks/use-custom-toast';
import useMediaQuery from '@/hooks/use-media-query';

function getEntitiesQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['entities', { slug }],
    queryFn: async () => {
      return await EntitiesService.readEntities({ companySlug: slug });
    },
  };
}

function getIndicatorsProgressionQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['indicators_progression'],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorsProgression({
        companySlug: slug,
      });
    },
  };
}

function getIndicatorsPeriodsQueryOptions({
  slug,
  year,
  entity_id = undefined,
  filter,
  sort,
  search,
}: {
  slug: string;
  year: number;
  entity_id?: string;
  entity_name?: string;
  filter: string;
  sort: string;
  search: string;
}) {
  return {
    queryKey: ['indicators_periods', { entity_id, filter, sort, search }],
    queryFn: async () => {
      const filterPath = entity_id
        ? 'indicator_year_entity___indicator_year_setting___indicator___category'
        : 'indicator_year_setting___indicator___category';

      const filtersWithCategory = `${filterPath}=simple`;

      const res = await IndicatorsPeriodsService.readIndicatorPeriods({
        year,
        entityId: entity_id,
        companySlug: slug,
        filters: filtersWithCategory,
        offset: 0,
        limit: 9999,
        sort,
        search,
      });

      return res;
    },
    retry: true,
  };
}

const parseIndicatorFile = async (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const result = e.target?.result;

        let workbook;

        if (file.name.endsWith('.csv')) {
          workbook = XLSX.read(result as string, {
            type: 'string',
            FS: ';',
          });
        } else {
          workbook = XLSX.read(result as ArrayBuffer, {
            type: 'array',
          });
        }

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });

        const formattedData = jsonData.map((row: any) => ({
          id: row['id'],
          value: parseFloat(row['value'] ?? row['Valeur']) || null,
          objective: parseFloat(row['objective'] ?? row['Objectif']) || null,
        }));

        resolve(formattedData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file, 'utf-8');
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

export const CollectImportDialog = ({ default_entity_id = 'consolidate' }: { default_entity_id?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { slug, year } = useParams({ strict: false });
  const { filter, search, sort } = useSearch({ strict: false });
  const [entityIdSelected, setEntityIdSelected] = useState<string[]>([default_entity_id]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showToast = useCustomToast();
  const [progress, setProgress] = useState(0);
  const [disableCalculate, setDisableCalculate] = useState(false);

  const { data: indicatorsProgression, isSuccess: indicatorsSuccess } = useQuery({
    ...getIndicatorsProgressionQueryOptions({ slug }),
  });

  useMemo(() => {
    setEntityIdSelected([!!default_entity_id ? default_entity_id : 'consolidate']);
  }, [default_entity_id]);

  const {
    data: entities,
    isLoading: isLoadingEntities,
    isSuccess: entitiesSuccess,
  } = useQuery({
    ...getEntitiesQueryOptions({ slug }),
  });

  const entitiesByYear = useMemo(() => {
    if (!indicatorsSuccess || !entitiesSuccess || !year) return [];

    const filteredIndicators = indicatorsProgression.data.filter((item) => item.year === parseInt(year, 10));

    const allEntities = filteredIndicators
      .flatMap((item) => item.entities ?? [])
      .sort((a, b) => a.name.localeCompare(b.name));

    const uniqueEntities = [...new Map(allEntities.map((e) => [e?.id, e])).values()];

    return uniqueEntities.map((e) => {
      const matched = entities.data.find((ent) => ent.id === e?.id);
      const entity = matched ?? e;
      return {
        name: entity.name,
        value: entity.id,
      };
    });
  }, [indicatorsProgression, year, entities, indicatorsSuccess, entitiesSuccess]);

  const createEntitiesCollection = (entities: { name: string; value: string }[]) => {
    if (!entities) return;

    return createListCollection({
      items: [
        { label: 'Consolidé', value: 'consolidate' },
        ...entities.map((entity) => ({
          label: entity.name,
          value: entity.value,
        })),
      ],
    });
  };

  const entitiesFramework = useMemo(() => {
    if (!entitiesByYear || entitiesByYear.length === 0) {
      setEntityIdSelected(['consolidate']);
      return undefined;
    }
    return createEntitiesCollection(entitiesByYear);
  }, [entitiesByYear]);

  const isCsvOrXlsxFile = (f: File) =>
    f.type === 'text/csv' ||
    f.type === 'application/vnd.ms-excel' ||
    f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    f.name.endsWith('.csv') ||
    f.name.endsWith('.xlsx');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];

      if (!isCsvOrXlsxFile(selected)) {
        setFile(null);
        showToast('Format de fichier incorrect', 'Veuillez séléctionner uniquement des fichiers excel', 'error');
        return;
      }

      setFile(selected);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];

    if (droppedFile) {
      if (!isCsvOrXlsxFile(droppedFile)) {
        setFile(null);
        return;
      }

      setFile(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const transformFields = (row: IndicatorYearPeriodPublic): Record<string, any> => {
    const formatNumberField = (val?: string | number | null) => {
      if (val === null || val === undefined) return '';
      return String(val).replace(/,/g, '.').replace(/\s+/g, '');
    };
    return {
      id: row.id ?? '',
      indicator_name: row.indicator_name?.trim() ?? '',
      indicator_id: row.indicator_id ?? '',
      commitment_name: row.commitment_name?.trim() ?? '',
      pillar_name: row.pillar_name?.trim() ?? '',
      value: formatNumberField(row.value),
      objective: formatNumberField(row.objective),
      indicator_unit: row.indicator_unit?.trim() ?? '',
    };
  };

  const generateTemplate = async () => {
    try {
      setIsLoading(true);
      setProgress(0);

      const hasNoSelection = entityIdSelected[0] === 'consolidate';
      const baseQuery = getIndicatorsPeriodsQueryOptions({
        slug,
        year,
        filter,
        sort,
        search,
      });

      const selectedEntities = entities?.data.filter((entity) => entityIdSelected.includes(entity.id)) ?? [];

      const allQueries = hasNoSelection
        ? [baseQuery]
        : selectedEntities.map((entity) =>
            getIndicatorsPeriodsQueryOptions({
              slug,
              year: parseInt(year),
              entity_id: selectedEntities[0].id,
              entity_name: entity.name,
              filter,
              sort,
              search,
            })
          );

      const total = allQueries.length;
      let completed = 0;
      const allData: IndicatorYearPeriodPublic[] = [];

      await Promise.all(
        allQueries.map(async (query) => {
          try {
            const result = await queryClient.fetchQuery(query);

            const items = Array.isArray(result) ? result : (result?.data ?? []);

            allData.push(...items);
          } catch (error) {
            console.error('Query failed:', error);
          } finally {
            completed += 1;
            setProgress(completed / total);
          }
        })
      );

      if (!allData || allData.length === 0) {
        alert('Aucune donnée à exporter.');
        setIsLoading(false);
        return;
      }

      const cleanedData = allData.filter((item) => item.id !== null);

      const flatData = cleanedData.filter((item) => item).map((item) => transformFields(item));

      const worksheet = XLSX.utils.json_to_sheet(flatData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Indicators');

      const now = new Date();
      const timestamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
      const filename = `indicators_${year}_collecte_${timestamp}_${hasNoSelection ? 'consolidated' : selectedEntities[0].name.replace(/\s+/g, '_')}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Erreur lors de la génération du template :', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} octets`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const mutation = useMutation({
    mutationFn: ({ id, value, objective }: { id: string; value: string; objective: string }) =>
      IndicatorsPeriodsService.updateIndicatorPeriod({
        id,
        companySlug: slug,
        requestBody: {
          value: Number.parseFloat(value),
          objective: Number.parseFloat(objective),
        },
        returnUpdated: false,
        disableCalculate,
      }),
  });

  const handleImport = async () => {
    if (!file) {
      showToast('Error', 'Veuillez sélectionner un fichier avant d’importer.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      setProgress(0);

      const rows = await parseIndicatorFile(file);

      let completed = 0;
      let failed = 0;
      const failedIds: string[] = [];

      for (const row of rows) {
        try {
          await mutation.mutateAsync({
            id: row.id,
            value: String(row.value),
            objective: String(row.objective),
          });

          completed++;
          setProgress(completed / rows.length);
        } catch (error) {
          failed++;
          failedIds.push(row.id);
          console.error(`❌ Erreur sur la ligne ID=${row.id}`, error);
        }
      }

      if (failed === 0) {
        showToast('Success', 'Toutes les périodes ont été mises à jour avec succès 🎉', 'success');
        queryClient.invalidateQueries({ queryKey: ['indicators_periods'] });
        queryClient.invalidateQueries({ queryKey: ['indicators_progression'] });
      } else if (failed < rows.length) {
        showToast('Warning', `${completed} période(s) mise(s) à jour avec succès, ${failed} échec(s).`, 'error');
      } else {
        showToast('Error', 'Toutes les mises à jour ont échoué', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l’import :', error);
      showToast('Error', "Une erreur est survenue pendant l'import.", 'error');
      setFile(null);
      setIsLoading(false);
    } finally {
      setFile(null);
      setIsLoading(false);
    }
  };

  const isTooltipHidden = useMediaQuery('(min-width: 1250px)');

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        setIsOpen(e.open);
      }}
      placement="center"
      size="lg"
    >
      <Dialog.Trigger as="div">
        <Tooltip
          showArrow
          content={t('ui.import')}
          openDelay={100}
          closeDelay={100}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          disabled={isTooltipHidden}
        >
          <Button variant="outline" size="sm" {...(!isTooltipHidden && { p: '0' })}>
            <CiImport />
            {isTooltipHidden && <span>{t('ui.import')}</span>}
          </Button>
        </Tooltip>
      </Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header flex="flex" flexDirection="column">
              <Dialog.Title>Import de données pour l'année {year}</Dialog.Title>
              <Text mb="2">Télécharger un modèle, remplissez-le, puis importez-le pour mettre à jour vos données</Text>
            </Dialog.Header>

            <Dialog.Body>
              <VStack rowGap={3} alignItems="start">
                <HStack w="full" justifyContent="space-between" alignItems={'start'}>
                  {!isLoadingEntities && entitiesFramework && (
                    <>
                      <VStack alignItems="start">
                        <Text fontWeight="bold" fontSize="sm">
                          Étape n°1 : Séléctionner le perimetre
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Séléctionner le perimetre ou se fera l'import
                        </Text>
                      </VStack>
                      <HStack justifyContent={'flex-start'}>
                        <Select.Root
                          collection={entitiesFramework}
                          width="300px"
                          value={entityIdSelected}
                          onValueChange={(e) => setEntityIdSelected(e.value)}
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText placeholder="Selectionner le perimetre" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Select.Positioner>
                            <Select.Content>
                              {entitiesFramework.items.map((framework) => (
                                <Select.Item item={framework} key={framework.value}>
                                  {framework.label}
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Root>
                      </HStack>
                    </>
                  )}
                </HStack>
                <HStack w="full" justifyContent="space-between">
                  <VStack alignItems="start">
                    <Text fontWeight="bold" fontSize="sm">
                      Étape n°{entitiesByYear.length ? '2' : '1'} : Télécharger le modèle
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Obtenez le fichier modèle à remplir
                    </Text>
                  </VStack>
                  <Button
                    variant="outline"
                    size="sm"
                    border="1px solid #1FA285"
                    onClick={generateTemplate}
                    color="#1FA285"
                    _hover={{ bg: 'primary.500', color: 'white' }}
                  >
                    <CiImport />
                    Télécharger
                  </Button>
                </HStack>

                <Separator />
                <VStack alignItems="start" w="full">
                  <HStack w="full" justifyContent="space-between">
                    <Box>
                      <Text fontWeight="bold" fontSize="sm">
                        Étape n°{entitiesByYear.length ? '3' : '2'} : Importer le fichier rempli
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Glissez-déposez ou cliquez pour sélectionner
                      </Text>
                    </Box>
                    <Switch.Root checked={disableCalculate} onCheckedChange={(e) => setDisableCalculate(e.checked)}>
                      <Switch.HiddenInput />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Désactiver les calcules automatiques</Switch.Label>
                    </Switch.Root>
                  </HStack>

                  <Box
                    w="full"
                    h="120px"
                    border="2px dashed #1FA285"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                    bg={file ? 'green.50' : 'gray.50'}
                    transition="background 0.2s ease"
                    _hover={{ bg: 'gray.100' }}
                  >
                    <VStack>
                      <CiImport />
                      <Text fontSize="xl">Cliquer ou glisser-déposer votre fichier</Text>
                      <Text>Fichier format .csv ou .xlsx uniquement — Max 10Mo</Text>
                    </VStack>
                  </Box>

                  <input
                    type="file"
                    accept=".csv, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </VStack>

                {file && (
                  <>
                    <Separator />
                    <VStack alignItems="start" w="full">
                      <Text fontWeight="bold" fontSize="sm">
                        Étape n°3 : Vérification du fichier importé
                      </Text>

                      <VStack
                        w="full"
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        bg="gray.50"
                        alignItems="start"
                      >
                        <HStack columnGap={3} alignItems="center" justifyContent="space-between" w="full">
                          <HStack>
                            <Box backgroundColor="#E6EAF3" padding={3}>
                              <BiFile color="#284EA7" />
                            </Box>
                            <VStack align="start" rowGap={0}>
                              <Text fontWeight="medium">{file.name}</Text>
                              <Text fontSize="xs" color="gray.500">
                                Taille : {formatFileSize(file.size)}
                              </Text>
                            </VStack>
                          </HStack>

                          <IconButton size="sm" variant="ghost" colorScheme="red" onClick={handleRemoveFile}>
                            <IoMdCloseCircleOutline />
                          </IconButton>
                        </HStack>

                        {successMessage && (
                          <HStack bg="#F0FDF4" padding="0.5rem 0.5rem" w="full">
                            <AiOutlineCheckCircle />
                            <Text fontSize="sm" color="green.600">
                              {successMessage}
                            </Text>
                          </HStack>
                        )}
                        {errorMessage && (
                          <HStack bg="#FDF0F0" padding="0.5rem 0.5rem" w="full">
                            <AiOutlineWarning />
                            <Text fontSize="sm" color="red.600">
                              {errorMessage}
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </VStack>
                  </>
                )}
              </VStack>

              {isLoading && (
                <VStack
                  w="full"
                  alignItems="center"
                  justifyContent="center"
                  py={6}
                  bg="whiteAlpha.800"
                  borderRadius="md"
                >
                  <Text fontWeight="medium" color="gray.600">
                    Import en cours... ({Math.round(progress * 100)}%)
                  </Text>

                  <Box w="80%" bg="gray.200" borderRadius="full" overflow="hidden" h="8px">
                    <Box
                      w={`${Math.min(100, Math.round(progress * 100))}%`}
                      h="8px"
                      bg="#1FA285"
                      transition="width 0.2s ease"
                    />
                  </Box>

                  <Text fontSize="xs" color="gray.500">
                    Ne fermez pas cette fenêtre pendant l’import.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">{t('ui.cancel')}</Button>
              </Dialog.ActionTrigger>
              <Button
                loading={isLoading}
                onClick={handleImport}
                loadingText="Import"
                spinnerPlacement="end"
                disabled={!file}
              >
                Importer
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
